import React, { useState, useEffect } from 'react';
import { useNotification, NOTIFICATION_TYPES, NOTIFICATION_CATEGORIES } from '../../context/NotificationContext';
import { 
  HiOutlineCheckCircle, HiOutlineXCircle, HiOutlineExclamationTriangle, 
  HiOutlineInformationCircle, HiOutlineXMark,
  HiOutlineBanknotes, HiOutlineUserPlus, HiOutlineWallet, HiOutlineBuildingLibrary,
  HiOutlineIdentification, HiOutlineUser, HiOutlineShieldCheck, HiOutlineUserGroup
} from 'react-icons/hi2';

const TYPE_STYLES = {
  [NOTIFICATION_TYPES.SUCCESS]: {
    bg: 'bg-green-50 border-green-200',
    icon: 'text-green-500',
    title: 'text-green-800',
    message: 'text-green-700'
  },
  [NOTIFICATION_TYPES.ERROR]: {
    bg: 'bg-red-50 border-red-200',
    icon: 'text-red-500',
    title: 'text-red-800',
    message: 'text-red-700'
  },
  [NOTIFICATION_TYPES.WARNING]: {
    bg: 'bg-amber-50 border-amber-200',
    icon: 'text-amber-500',
    title: 'text-amber-800',
    message: 'text-amber-700'
  },
  [NOTIFICATION_TYPES.INFO]: {
    bg: 'bg-blue-50 border-blue-200',
    icon: 'text-blue-500',
    title: 'text-blue-800',
    message: 'text-blue-700'
  }
};

const TYPE_ICONS = {
  [NOTIFICATION_TYPES.SUCCESS]: HiOutlineCheckCircle,
  [NOTIFICATION_TYPES.ERROR]: HiOutlineXCircle,
  [NOTIFICATION_TYPES.WARNING]: HiOutlineExclamationTriangle,
  [NOTIFICATION_TYPES.INFO]: HiOutlineInformationCircle
};

const CATEGORY_ICONS = {
  [NOTIFICATION_CATEGORIES.PAYMENT]: HiOutlineBanknotes,
  [NOTIFICATION_CATEGORIES.SIGNUP]: HiOutlineUserPlus,
  [NOTIFICATION_CATEGORIES.EXTERNAL_ACCOUNT]: HiOutlineWallet,
  [NOTIFICATION_CATEGORIES.BANK]: HiOutlineBuildingLibrary,
  [NOTIFICATION_CATEGORIES.KYC]: HiOutlineIdentification,
  [NOTIFICATION_CATEGORIES.PROFILE]: HiOutlineUser,
  [NOTIFICATION_CATEGORIES.SECURITY]: HiOutlineShieldCheck,
  [NOTIFICATION_CATEGORIES.TEAM]: HiOutlineUserGroup
};

const NotificationItem = ({ notification, onRemove }) => {
  const styles = TYPE_STYLES[notification.type] || TYPE_STYLES[NOTIFICATION_TYPES.INFO];
  const CategoryIcon = CATEGORY_ICONS[notification.category];
  const TypeIcon = TYPE_ICONS[notification.type];
  const Icon = CategoryIcon || TypeIcon;

  return (
    <div className={`flex items-start gap-3 p-4 rounded-xl border shadow-lg ${styles.bg} animate-slide-in`}>
      <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${styles.icon}`} />
      <div className="flex-1 min-w-0">
        <p className={`font-semibold text-sm ${styles.title}`}>{notification.title}</p>
        {notification.message && (
          <p className={`text-sm mt-0.5 ${styles.message}`}>{notification.message}</p>
        )}
      </div>
      <button onClick={() => onRemove(notification.id)} className="p-1 hover:bg-black/5 rounded-lg transition-colors">
        <HiOutlineXMark className="w-4 h-4 text-slate-500" />
      </button>
    </div>
  );
};

const NotificationToast = () => {
  const { notifications } = useNotification();
  const [visibleToasts, setVisibleToasts] = useState(new Set());
  const [hiddenToasts, setHiddenToasts] = useState(new Set());

  // Track new notifications and auto-hide after 7 seconds
  // Only show toast for notifications with isNew: true
  useEffect(() => {
    notifications.forEach(n => {
      // Only show toast for NEW notifications (not loaded from database)
      if (n.isNew && !visibleToasts.has(n.id) && !hiddenToasts.has(n.id)) {
        setVisibleToasts(prev => new Set([...prev, n.id]));
        
        // Auto-hide toast after 7 seconds
        setTimeout(() => {
          setVisibleToasts(prev => {
            const next = new Set(prev);
            next.delete(n.id);
            return next;
          });
          setHiddenToasts(prev => new Set([...prev, n.id]));
        }, 7000);
      }
    });
  }, [notifications, visibleToasts, hiddenToasts]);

  const handleDismiss = (id) => {
    setVisibleToasts(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setHiddenToasts(prev => new Set([...prev, id]));
  };

  const toastsToShow = notifications.filter(n => visibleToasts.has(n.id));

  if (toastsToShow.length === 0) return null;

  return (
    <div className="fixed top-20 right-4 z-[9999] flex flex-col gap-3 max-w-sm w-full pointer-events-none">
      {toastsToShow.map(notification => (
        <div key={notification.id} className="pointer-events-auto">
          <NotificationItem notification={notification} onRemove={handleDismiss} />
        </div>
      ))}
    </div>
  );
};

export default NotificationToast;

